// Shared Reviews Component - Reusable across all pages
// Shows Facebook reviews with link to see more
import { motion } from "framer-motion";
import { Star, ArrowRight } from "lucide-react";

// Facebook icon component
const FacebookIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

// Master list of all reviews - single source of truth
export const ALL_REVIEWS = [
  {
    id: 1,
    quote: "Annelie's yin yoga is really rewarding and enjoyable. I always leave her classes feeling relaxed and totally de-stressed both physically and mentally. Annelie's Women's Circles are interesting and fun, and I have made a lot of friends by going to the Circles.",
    fullQuote: "Annelie's yin yoga is really rewarding and enjoyable. I always leave her classes feeling relaxed and totally de-stressed both physically and mentally. Annelie's Women's Circles are interesting and fun, and I have made a lot of friends by going to the Circles. I have enjoyed many yoga retreats giving me a break away and a sense of belonging. I have even been abroad on the Spain yoga retreat which, for me, was life changing. We all always have so much fun together and I am so happy that I have made new friends and done all sorts of things as a group together such as exploring sacred places, saunas at Wild Cat, and dancing in Frome. This year we are going on a pilgrimage to Egypt and I absolutely cannot wait!!",
    name: "H",
    location: "Somerset",
    tags: ["yin-yoga", "womens-circles", "retreats", "community", "yoga-classes", "hatha-yoga"],
    source: "facebook"
  },
  {
    id: 2,
    quote: "The beauty about this community is that everyone is welcomed as their individual self — however, when we come together in a group setting there is a sense of unity, mutual trust, respect and understanding.",
    fullQuote: "I have been going to Annelie's yin yoga, women's circle and retreats for well over a year now. During this time, I have met some amazing people who have helped me through some very challenging times in my life. The beauty about this community is that everyone is welcomed as their individual self — however, when we come together in a group setting there is a sense of unity, mutual trust, respect and understanding. No matter where you are on your journey, you have the freedom to be your authentic self without judgement or fear of rejection. Annelie is an amazing teacher who brings forth her knowledge, experience and passion and I am so grateful to be part of this amazing community.",
    name: "A",
    location: "Exeter",
    tags: ["yin-yoga", "womens-circles", "retreats", "community", "yoga-classes", "treatments", "reiki", "healing"],
    source: "facebook"
  },
  {
    id: 3,
    quote: "A truly wonderful and life changing experience. Annelie is a wonderful teacher, her knowledge is fascinating. She took care with everyone with differing abilities and has a very soothing, calming voice. 11/10 for the whole experience.",
    fullQuote: "A truly wonderful and life changing experience. I was not into yoga before I went on the retreat but hoped to get something out of the experience that I resonated with — which I did, and so much more. I enjoyed every single yoga session over the weekend. Annelie is a wonderful teacher, her knowledge is fascinating. She took care with everyone with differing abilities and has a very soothing, calming voice and made sure everyone was comfortable. 11/10 for the whole experience.",
    name: "H",
    location: "Exeter",
    type: "Retreat participant",
    tags: ["retreats", "beginners", "yoga-classes", "hatha-yoga", "yin-yoga"],
    source: "facebook"
  }
];

// Get reviews by tags
export const getReviewsByTags = (tags, limit = 3) => {
  const filtered = ALL_REVIEWS.filter(review => 
    review.tags.some(tag => tags.includes(tag))
  );
  return filtered.slice(0, limit);
};

// Get reviews for specific page types
export const getReviewsForPage = (pageType, limit = 3) => {
  const tagMap = {
    'homepage': ['yin-yoga', 'retreats', 'community'],
    'yoga-classes': ['hatha-yoga', 'yin-yoga', 'yoga-classes'],
    'hatha-yoga': ['hatha-yoga', 'yoga-classes'],
    'yin-yoga': ['yin-yoga', 'yoga-classes'],
    'treatments': ['reiki', 'treatments', 'healing'],
    'reiki': ['reiki', 'treatments', 'healing'],
    'womens-circles': ['womens-circles', 'community'],
    'retreats': ['retreats', 'beginners'],
    'about': ['community', 'retreats', 'yoga-classes']
  };
  
  const tags = tagMap[pageType] || ['yoga-classes', 'community'];
  return getReviewsByTags(tags, limit);
};

// Single Review Card Component
export const ReviewCard = ({ review, showFacebookIcon = true, compact = false }) => (
  <div className={`bg-white/70 ${compact ? 'p-4' : 'p-6'} rounded-xl border border-gold/10`}>
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-1">
        {[...Array(5)].map((_, i) => (
          <Star key={i} className={`${compact ? 'w-3 h-3' : 'w-4 h-4'} text-gold fill-gold`} />
        ))}
      </div>
      {showFacebookIcon && review.source === "facebook" && (
        <FacebookIcon className={`${compact ? 'w-3 h-3' : 'w-4 h-4'} text-[#1877F2]`} />
      )}
    </div>
    <p className={`text-neutral-700 ${compact ? 'text-xs' : 'text-sm'} leading-relaxed mb-3 italic ${compact ? 'line-clamp-3' : 'line-clamp-4'}`}>
      "{review.quote}"
    </p>
    <div className="border-t border-gold/10 pt-3">
      <p className={`font-medium text-neutral-800 ${compact ? 'text-xs' : 'text-sm'}`}>
        — {review.name}, {review.location}
      </p>
      {review.type && (
        <p className="text-neutral-500 text-xs">{review.type}</p>
      )}
    </div>
  </div>
);

// Full Reviews Section Component (for main pages)
export const ReviewsSection = ({ 
  reviews = ALL_REVIEWS.slice(0, 3),
  title = "What People Say",
  subtitle = "Reviews from our Facebook community",
  showFacebookButton = true,
  columns = 3,
  className = ""
}) => (
  <section className={`py-16 px-6 md:px-12 bg-muted ${className}`} data-testid="reviews-section">
    <div className="max-w-6xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className="text-center mb-10"
      >
        <span className="text-xs uppercase tracking-[0.2em] text-gold mb-2 block">Testimonials</span>
        <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-2">{title}</h2>
        {subtitle && (
          <div className="flex items-center justify-center gap-2 mt-3">
            <FacebookIcon className="w-4 h-4 text-[#1877F2]" />
            <span className="text-neutral-500 text-sm">{subtitle}</span>
          </div>
        )}
      </motion.div>

      <div className={`grid ${columns === 3 ? 'md:grid-cols-3' : columns === 2 ? 'md:grid-cols-2' : ''} gap-6`}>
        {reviews.map((review, index) => (
          <motion.div
            key={review.id || index}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            <ReviewCard review={review} />
          </motion.div>
        ))}
      </div>

      {showFacebookButton && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="text-center mt-10"
        >
          <a
            href="https://www.facebook.com/seventhsensehealing/reviews"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 px-6 py-3 bg-[#1877F2] text-white rounded-full hover:bg-[#1664d9] transition-colors group"
            data-testid="facebook-reviews-link"
          >
            <FacebookIcon className="w-5 h-5" />
            <span className="font-medium">See All Reviews on Facebook</span>
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </a>
        </motion.div>
      )}
    </div>
  </section>
);

// Compact Reviews Strip (for service pages)
export const ReviewsStrip = ({ 
  reviews,
  pageType,
  title = "What Our Community Says"
}) => {
  const displayReviews = reviews || getReviewsForPage(pageType, 2);
  
  return (
    <section className="py-12 px-6 md:px-12 bg-cream" data-testid="reviews-strip">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex items-center justify-center gap-2 mb-6">
            <FacebookIcon className="w-4 h-4 text-[#1877F2]" />
            <h3 className="font-heading text-lg text-neutral-800">{title}</h3>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4">
            {displayReviews.map((review, index) => (
              <ReviewCard key={review.id || index} review={review} compact />
            ))}
          </div>
          
          <div className="text-center mt-6">
            <a
              href="https://www.facebook.com/seventhsensehealing/reviews"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-[#1877F2] hover:underline inline-flex items-center gap-1"
            >
              See more reviews on Facebook <ArrowRight className="w-3 h-3" />
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ReviewsSection;
